<template>
<PageContent>
  <Container>
    <template v-if="editing">
      <CustomerEdit
        :customer="customerInfo"
        :child-info-flag="childInfoFlag"
        :editCancel="cancel"
        :complete="updateComplete"
      />
    </template>
    <template v-else>
      <NoticeBox type="info" v-if="editCompleted">
        お客様の登録情報を更新しました。
      </NoticeBox>
      <CustomerConfirm
        :customer="customerInfo"
        :child-info-flag="childInfoFlag"
      />
      <div class="form-footer text-center">
        <WaveButton type="button" class="button" @click="edit">
          編集する
        </WaveButton>
      </div>
    </template>
  </Container>
</PageContent>
</template>

<script>
import NoticeBox from '@/components/NoticeBox.vue'
import CustomerEdit from '@/components/CustomerEdit.vue'
import CustomerConfirm from '@/components/CustomerConfirm.vue'
import officeApi from '@/api/office'
import customerApi from '@/api/customer'
import { renderError } from '@/utils/error'

export default {
  props: {
    groomOrBride: {
      type: Number,
      required: true,
      default: 1,
    }
  },
  components: {
    NoticeBox,
    CustomerEdit,
    CustomerConfirm,
  },
  data() {
    return {
      prefectures: [],
      office: {},
      editing: false,
      editCompleted: false,
      childInfoFlag: false,
      childInfo: [],
      customerInfo: {},
      customerKeys: [
        'groom',
        'bride',
        'groom_father',
        'groom_mother',
        'bride_father',
        'bride_mother',
      ],
    }
  },
  computed: {
    accountId() {
      return this.$store.getters['auth/userId']
    },
    businessId() {
      return this.$store.getters['auth/businessId']
    }
  },
  methods: {
    edit() {
      this.editing = true
      window.scrollTo(0, 0)
    },
    async fetch() {
      try {
        // 顧客情報取得
        this.customerInfo = await customerApi.show(this.accountId)
        this.customerInfo.groom.telPriorityNo = this.getPriorityTelItemNo(this.customerInfo.groom)
        this.customerInfo.bride.telPriorityNo = this.getPriorityTelItemNo(this.customerInfo.bride)

        for (let idx = 0; idx < this.customerKeys.length; idx++) {
          this.createName(this.customerInfo[this.customerKeys[idx]])
        }

        // 対象の新郎新婦情報を保持
        if (this.groomOrBride === 1) {
          this.customerInfo.groomBride = this.customerInfo.groom
          this.customerInfo.father = this.customerInfo.groom_father
          this.customerInfo.mother = this.customerInfo.groom_mother
        } else {
          this.customerInfo.groomBride = this.customerInfo.bride
          this.customerInfo.father = this.customerInfo.bride_father
          this.customerInfo.mother = this.customerInfo.bride_mother
        }

        // お子様情報を整形
        for (let child = 1; child <= 5; child++) {
          this.childInfo.push({
            'sex': this.customerInfo.groomBride.child_info[`child${child}_sex`],
            'year': this.customerInfo.groomBride.child_info[`child${child}_year`],
            'month': this.customerInfo.groomBride.child_info[`child${child}_month`],
          })
        }

        // 親御様の住所
        this.customerInfo.parentAddress = {
          'address1_postcode1': this.customerInfo.father.address1_postcode1,
          'address1_postcode2': this.customerInfo.father.address1_postcode2,
          'address1_billing_state': this.customerInfo.father.address1_billing_state,
          'address1_billing_city': this.customerInfo.father.address1_billing_city,
          'address1_billing_street': this.customerInfo.father.address1_billing_street,
          'address1_building_name': this.customerInfo.father.address1_building_name,
        }

        // 店舗情報取得
        this.office = await officeApi.get(this.businessId)
        this.childInfoFlag = this.office.child_info_flag > 0 ? true : false
      } catch(err) {
        renderError(err)
      }
    },
    createName(target) {
      // お名前
      if (target.last_name=== '') {
        target.name = target.last_name_kanji_old + target.first_name
      } else if (target.last_name !== target.last_name_kanji_old && target.last_name !== '' && target.last_name_kanji_old !== '') {
        target.name = target.last_name + '（' + target.last_name_kanji_old  + '）' + target.first_name
      } else {
        target.name = target.last_name + target.first_name
      }
      // お名前(フリガナ)
      if (target.last_name_kana === '') {
        target.name_kana = target.last_name_kana_old + target.first_name_kana
      } else if (target.last_name_kana !== target.last_name_kana_old && target.last_name_kana !== '' && target.last_name_kana_old !== '') {
        target.name_kana = target.last_name_kana + '（' + target.last_name_kana_old  + '）' + target.first_name_kana
      } else {
        target.name_kana = target.last_name_kana + target.first_name_kana
      }
      // お名前(ローマ字)
      // まず姓のみを整形
      if (target.last_name_romaji === '') {
        target.name_romaji = target.last_name_romaji_old
      } else if (target.last_name_romaji !== target.last_name_romaji_old && target.last_name_romaji !== '' && target.last_name_romaji_old !== '') {
        target.name_romaji = target.last_name_romaji + '（' + target.last_name_romaji_old  + '）'
      } else {
        target.name_romaji = target.last_name_romaji
      }
      // 名を追加
      if (target.name_romaji === '' || target.first_name_romaji === '') {
        target.name_romaji += target.first_name_romaji
      } else {
        // 姓と名両方がある場合、間に半角スペースを入れる
        target.name_romaji += (' ' + target.first_name_romaji) 
      }
    },
    getPriorityTelItemNo (customer) {
      let priority = 1
      let tel1 = '' + customer['tel1_area_no'] + customer['tel1_local_no'] + customer['tel1_no']
      let tel2 = '' + customer['tel2_area_no'] + customer['tel2_local_no'] + customer['tel2_no']
      let tel3 = '' + customer['tel3_area_no'] + customer['tel3_local_no'] + customer['tel3_no']

      if (tel2 === '' || tel1 !== '' && parseInt(customer['tel1_priority_order']) <= parseInt(customer['tel2_priority_order'])) {
          priority = 1
      } else {
          priority = 2
      }

      if (tel3 === '' || eval('tel' + priority) !== '' && parseInt(customer['tel' + priority + '_priority_order']) <= parseInt(customer['tel3_priority_order'])) {
          // なにもしない
      } else {
          priority = 3
      }

      return priority
    },
    cancel() {
      this.editing = false
      this.editCompleted = false
    },
    updateComplete() {
      this.editing = false
      this.editCompleted = true
      // データを取得しなおす
      this.$withLoading(() => Promise.all([
        this.fetch()
      ]))
    }
  },
  created() {
    this.$withLoading(() => Promise.all([
      this.fetch()
    ]))
  }
}
</script>
